/**
 * The open project as text a model can read — short names, metres and degrees, walls by
 * the side of the room they close.
 *
 * Two forms: `briefScene` is the project map that rides along every chat message so the
 * agent knows where everything is without a round trip; `describeScene` is the `get_scene`
 * tool's listing with the exact geometry.
 */
import type { Item, Level, Opening, Room, Scene, Surface, Wall } from '../core/hp/types';
import type { EditorState } from '../state/store';
import { levelLabel } from '../state/levelTitles';
import { aliases, aliasScope, type AliasKind } from './aliases';
import { roomWalls, SIDE_LETTER, wallSide } from './layout';

export const fmt = (value: number): string => {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/0$/, '');
};
/**
 * Agent degrees are the properties sheet's degrees: the negated yaw, whole, in [0, 360).
 * At 0 the front faces +z (down on the plan), 90 faces +x, 180 faces −z, 270 faces −x.
 */
export const toDegrees = (radians: number): number => ((Math.round(-radians * 180 / Math.PI) % 360) + 360) % 360;
export const toRadians = (degrees: number): number => -degrees * Math.PI / 180;
/** Unit vector the item's front points along on the plan, for a yaw in radians. */
export const frontOf = (radians: number): { x: number; z: number } => ({ x: -Math.sin(radians), z: Math.cos(radians) });

export type Namer = (kind: AliasKind, id: string) => string;

const hex = (channel: number) => Math.round(Math.max(0, Math.min(1, channel)) * 255).toString(16).padStart(2, '0');
export function surfaceLabel(surface: Surface | undefined): string {
  if (!surface) return 'default';
  if (surface.caption) return surface.caption;
  if (surface.customTexture) return 'custom image';
  if (surface.texture) return surface.texture.split('/').pop()?.replace(/\.[^.]+$/, '') ?? surface.texture;
  if (surface.color) return '#' + surface.color.map(hex).join('');
  return 'default';
}

/** What `add_item` would need to recreate the item: the catalog id for local models. */
export function catalogIdOf(item: Item): string {
  return item.modelRef.kind === 'cloud' ? 'cloud:' + item.modelRef.id : item.modelRef.kind === 'local' ? item.modelRef.id : 'none';
}

const point = (p: { x: number; z: number }) => `(${fmt(p.x)}, ${fmt(p.z)})`;
const bounds = (points: readonly { x: number; z: number }[]) => {
  const xs = points.map(p => p.x), zs = points.map(p => p.z);
  return `x ${fmt(Math.min(...xs))}…${fmt(Math.max(...xs))}, z ${fmt(Math.min(...zs))}…${fmt(Math.max(...zs))}`;
};
const extent = (points: readonly { x: number; z: number }[]) => {
  const xs = points.map(p => p.x), zs = points.map(p => p.z);
  return `${fmt(Math.max(...xs) - Math.min(...xs))}×${fmt(Math.max(...zs) - Math.min(...zs))}`;
};

export function describeRoom(room: Room): string {
  const outline = room.polygon.length <= 8 ? room.polygon.map(point).join(' ') : `${room.polygon.length} vertices, ${bounds(room.polygon)}`;
  return `- room #${room.index} "${room.name || 'Room'}"${room.roomType ? ` (${room.roomType})` : ''}, ${fmt(room.area)} m², height ${fmt(room.height)} m, outline: ${outline}; floor: ${surfaceLabel(room.floorSurface)}; ceiling: ${surfaceLabel(room.ceilingSurface)}`;
}

export function describeWall(wall: Wall, name: Namer, compact = false, room?: Room): string {
  const side = room ? ` ${SIDE_LETTER[wallSide(room, wall)]} of #${room.index}` : '';
  const geometry = `- ${name('wall', wall.id)}${side}: ${point(wall.start)}→${point(wall.end)}, length ${fmt(wall.length)}, height ${fmt(wall.height)}, thickness ${fmt(wall.thickness)}; room #${wall.roomIndex}`;
  return compact ? geometry : `${geometry}; front: ${surfaceLabel(wall.frontSurface)}; back: ${surfaceLabel(wall.backSurface)}`;
}

export function describeOpening(opening: Opening, name: Namer): string {
  return `- ${name('opening', opening.id)}: ${opening.kind ?? opening.caption ?? 'opening'} on ${name('wall', opening.wallId)}, offset ${fmt(opening.offset)}, ${fmt(opening.width)}×${fmt(opening.height)} m, sill ${fmt(opening.sill)}${opening.flipped ? ', flipped' : ''}${opening.design?.style ? `, ${opening.design.style}` : ''}`;
}

export function describeItem(item: Item, name: Namer, compact = false): string {
  const slots = compact ? [] : Object.keys(item.materials ?? {}).filter(slot => slot !== '*');
  const parts = [
    `- ${name('item', item.id)}: "${item.caption || 'item'}" [${catalogIdOf(item)}]`,
    `at ${point(item.position)}, rotation ${toDegrees(item.rotation)}°, size ${fmt(item.size.w)}×${fmt(item.size.d)}×${fmt(item.size.h)} m (w×d×h)`,
    item.placement !== 'floor' ? `${item.placement}${item.wallId ? ` on ${name('wall', item.wallId)}` : ''}` : '',
    item.elevation > 0.001 ? `elevation ${fmt(item.elevation)}` : '',
    item.mirrored ? 'mirrored' : '',
    slots.length ? `slots: ${slots.slice(0, 6).join(', ')}${slots.length > 6 ? `, +${slots.length - 6}` : ''}` : '',
  ];
  return parts.filter(Boolean).join(', ');
}

export interface DescribeOptions { level?: number; roomIndex?: number; include?: readonly ('rooms' | 'walls' | 'openings' | 'items')[]; /** `full` adds finishes and material slots; the default keeps names and geometry only. */ detail?: 'compact' | 'full'; /** The project the scene belongs to, so names match the map. */ projectId?: string | null }
/** Past this many items the compact listing asks for a room instead of dumping the floor. */
export const COMPACT_ITEM_LIMIT = 60;
/** Past this many items in one room the map shows the first ones and counts the rest. */
export const MAP_ITEM_LIMIT = 30;

/** True when the point lies inside the polygon (edges count as inside). */
export function insidePolygon(polygon: readonly { x: number; z: number }[], p: { x: number; z: number }): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j], b = polygon[i];
    if ((a.z > p.z) !== (b.z > p.z) && p.x < (b.x - a.x) * (p.z - a.z) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}

export function roomOfPoint(level: Level, p: { x: number; z: number }): Room | undefined {
  const inside = level.rooms.filter(room => room.polygon.length >= 3 && insidePolygon(room.polygon, p));
  // The smallest room wins so a nested room is reported over the one around it.
  return inside.sort((a, b) => a.area - b.area)[0];
}

/** The room an item belongs to: the one its wall bounds, else the one around its footprint centre. */
export function roomOfItem(level: Level, item: Item): Room | undefined {
  if (item.wallId) {
    const wall = level.walls.find(candidate => candidate.id === item.wallId);
    if (wall) return level.rooms.find(room => room.index === wall.roomIndex) ?? level.rooms.find(room => roomWalls(level, room).includes(wall));
  }
  return roomOfPoint(level, item.position);
}

export function describeScene(scene: Scene, activeLevel: number, options: DescribeOptions = {}): string {
  const levelIndex = options.level ?? activeLevel;
  const level = scene.levels[levelIndex];
  if (!level) return `No floor ${levelIndex}; the project has ${scene.levels.length} floor(s).`;
  const name = aliases.forScope(aliasScope(options.projectId ?? scene.projectId, levelIndex), level);
  const include = new Set(options.include?.length ? options.include : ['rooms', 'walls', 'openings', 'items']);
  const room = options.roomIndex !== undefined ? level.rooms.find(value => value.index === options.roomIndex) : undefined;
  if (options.roomIndex !== undefined && !room) return `No room #${options.roomIndex} on floor ${levelIndex}. Rooms: ${level.rooms.map(r => `#${r.index}`).join(', ') || 'none'}.`;
  const lines: string[] = [];
  lines.push(`Floor ${levelIndex} "${levelLabel(scene.levels, levelIndex)}" (${levelIndex + 1} of ${scene.levels.length}), height ${fmt(level.height)} m${room ? `, room #${room.index} only` : ''}.`);
  const rooms = room ? [room] : level.rooms;
  const walls = room ? roomWalls(level, room) : level.walls;
  const wallIds = new Set(walls.map(wall => wall.id));
  const openings = room ? level.openings.filter(opening => wallIds.has(opening.wallId)) : level.openings;
  const items = room ? level.items.filter(item => item.wallId ? wallIds.has(item.wallId) : insidePolygon(room.polygon, item.position)) : level.items;
  if (include.has('rooms')) lines.push(rooms.length ? `Rooms (${rooms.length}):` : 'Rooms: none (draw walls or add_room first).', ...rooms.map(describeRoom));
  const compact = options.detail !== 'full';
  if (include.has('walls')) lines.push(walls.length ? `Walls (${walls.length}; side = which side of the room it closes; front = room side):` : 'Walls: none.',
    ...walls.map(wall => describeWall(wall, name, compact, room ?? level.rooms.find(candidate => candidate.index === wall.roomIndex))));
  if (include.has('openings')) lines.push(openings.length ? `Openings (${openings.length}):` : 'Openings: none.', ...openings.map(opening => describeOpening(opening, name)));
  if (include.has('items')) {
    const shown = compact && !room && items.length > COMPACT_ITEM_LIMIT ? items.slice(0, COMPACT_ITEM_LIMIT) : items;
    lines.push(items.length ? `Items (${items.length}${shown.length < items.length ? `, first ${shown.length} — pass room_index for the rest` : ''}):` : 'Items: none.', ...shown.map(item => describeItem(item, name, compact)));
    if (compact) lines.push('(detail: "full" adds finishes and material slots.)');
  }
  return lines.join('\n');
}

/** One wall of the map: its name, the side it closes, its ends and what is cut into it. */
function mapWall(level: Level, room: Room, wall: Wall, name: Namer): string {
  const cut = level.openings.filter(opening => opening.wallId === wall.id)
    .map(opening => `${name('opening', opening.id)} ${opening.kind ?? 'opening'} @${fmt(opening.offset)} ${fmt(opening.width)} m`);
  return `  ${name('wall', wall.id)} ${SIDE_LETTER[wallSide(room, wall)]} ${point(wall.start)}→${point(wall.end)} ${fmt(wall.length)} m${cut.length ? ` [${cut.join('; ')}]` : ''}`;
}

function mapItem(item: Item, name: Namer): string {
  const where = item.wallId ? `on ${name('wall', item.wallId)} h${fmt(item.elevation)}` : `@${point(item.position)} ${toDegrees(item.rotation)}°${item.placement === 'ceiling' ? ' ceiling' : item.elevation > 0.001 ? ` h${fmt(item.elevation)}` : ''}`;
  return `${name('item', item.id)} ${item.caption || 'item'} ${fmt(item.size.w)}×${fmt(item.size.d)} ${where}`;
}

/**
 * The project map that travels with every user message: every room with its walls by side,
 * the openings cut into them and the items inside — everything a tool call needs, named.
 */
export function briefScene(state: Pick<EditorState, 'scene' | 'activeLevelIndex' | 'projectName' | 'projectId' | 'mode' | 'firstPerson' | 'planSurface' | 'selection' | 'isolatedRoom'>): string {
  const scene = state.scene;
  if (!scene) return 'No project is open.';
  const level = scene.levels[state.activeLevelIndex];
  const view = state.mode === 'plan' ? `2D plan (${state.planSurface})` : state.firstPerson ? '3D walk (first person)' : '3D orbit';
  const lines = [`Project "${state.projectName || 'Project'}": ${scene.levels.length} floor(s); active floor ${state.activeLevelIndex} "${levelLabel(scene.levels, state.activeLevelIndex)}"; view: ${view}${state.isolatedRoom !== null ? `; only room #${state.isolatedRoom} shown` : ''}.`];
  if (level) {
    const name = aliases.forScope(aliasScope(state.projectId, state.activeLevelIndex), level);
    if (!level.rooms.length) lines.push('Rooms: none yet — the floor is empty (add_room).');
    const placed = new Set<string>();
    for (const room of level.rooms) {
      const items = level.items.filter(item => roomOfItem(level, item)?.index === room.index);
      items.forEach(item => placed.add(item.id));
      const shown = items.slice(0, MAP_ITEM_LIMIT);
      lines.push(`Room #${room.index} "${room.name || 'Room'}"${room.roomType ? ` (${room.roomType})` : ''}: ${extent(room.polygon)} m, ${bounds(room.polygon)}, ${fmt(room.area)} m², h ${fmt(room.height)}; floor: ${surfaceLabel(room.floorSurface)}`);
      for (const wall of roomWalls(level, room)) lines.push(mapWall(level, room, wall, name));
      lines.push(items.length ? `  items: ${shown.map(item => mapItem(item, name)).join('; ')}${items.length > shown.length ? `; +${items.length - shown.length} more (get_scene room_index ${room.index})` : ''}` : '  items: none');
    }
    const loose = level.walls.filter(wall => !level.rooms.some(room => roomWalls(level, room).includes(wall)));
    if (loose.length) lines.push(`Walls outside rooms: ${loose.map(wall => `${name('wall', wall.id)} ${point(wall.start)}→${point(wall.end)}`).join('; ')}`);
    const stray = level.items.filter(item => !placed.has(item.id));
    if (stray.length) lines.push(`Items outside rooms: ${stray.slice(0, MAP_ITEM_LIMIT).map(item => mapItem(item, name)).join('; ')}`);
    const selection = state.selection;
    if (selection?.kind === 'items') lines.push(`Selected: ${selection.ids.length} items ${selection.ids.map(id => name('item', id)).join(', ')}.`);
    else if (selection) lines.push(`Selected: ${selection.kind} ${'id' in selection ? name(selection.kind === 'item' ? 'item' : selection.kind === 'opening' ? 'opening' : 'wall', selection.id) : 'index' in selection ? '#' + selection.index : name('wall', selection.wallId)}.`);
  }
  return lines.join('\n');
}
